import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Switch,
  FormControlLabel,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BoardWithColumns } from 'api/todo/types';
import ConfirmationDialog from 'components/confirmationDialog/ConfirmationDialog';

interface BoardModalProps {
  open: boolean;
  mode: 'create' | 'manage';
  board?: BoardWithColumns;
  onClose: () => void;
  onSave: (name: string, isPrivate: boolean) => void;
  onDelete?: () => void;
}

export const BoardModal: React.FC<BoardModalProps> = ({
  open,
  mode,
  board,
  onClose,
  onSave,
  onDelete,
}) => {
  const [name, setName] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [deleteBoardConfirmOpen, setDeleteBoardConfirmOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) {
      if (mode === 'manage' && board) {
        setName(board.name);
        setIsPrivate(board.is_private);
      } else {
        setName('');
        setIsPrivate(true);
      }
    } else {
      // Reset form when modal closes
      setName('');
      setIsPrivate(true);
      setDeleteBoardConfirmOpen(false);
    }
  }, [open, mode, board]);

  const handleSave = async () => {
    if (!name.trim()) return;
    await onSave(name.trim(), isPrivate);
    // Modal will be closed by parent component after successful save
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {mode === 'create' ? 'Create Board' : 'Manage Board'}
          {isMobile && (
            <IconButton onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Board Settings */}
            <Box>
              <TextField
                label="Board Name"
                required
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!name.trim()}
              />
              <FormControlLabel
                control={<Switch checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />}
                label="Private"
                sx={{ mt: 2 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Private boards are only visible to you. Public boards are visible to all users.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {mode === 'manage' && onDelete && (
            <Button
              onClick={() => setDeleteBoardConfirmOpen(true)}
              color="error"
              sx={{ mr: 'auto', minHeight: { xs: 44, sm: 36 } }}
            >
              Delete Board
            </Button>
          )}
          <Button onClick={onClose} sx={{ minHeight: { xs: 44, sm: 36 } }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!name.trim()} sx={{ minHeight: { xs: 44, sm: 36 } }}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmations */}
      <ConfirmationDialog
        open={deleteBoardConfirmOpen}
        onClose={() => setDeleteBoardConfirmOpen(false)}
        onConfirm={() => {
          onDelete?.();
          setDeleteBoardConfirmOpen(false);
          onClose();
        }}
        title="Delete Board?"
        description={`This will permanently delete the board '${board?.name}' and all its tasks. This action cannot be undone.`}
        confirmText="Delete"
      />
    </>
  );
};
